<script lang="ts" setup>
import { mdiFilterOutline } from "@mdi/js";
import { useQuery, useMutation } from "@tanstack/vue-query";
import SwipeBetFilterModal from "./SwipeBetFilterModal.vue";
import api from "~/utils/auth/api";
import { useSettings } from "~/store/settings";
import { useAuth } from "~/store/auth";
import { IUpdateFilterData, IfilterData, ISelection } from "~/types/swipe-bet/filter";

const settings = useSettings();
const auth = useAuth();

const swipeFilterModal = ref<"type-sort" | "leagues" | "date" | "odds" | boolean>(false);

interface Props {
	loaded: boolean;
	swipeCount?: number;
	swipeBetsTotal: number;
}

interface ISwipeHeaderData {
	data: IUpdateFilterData[];
	total: number;
}

const { loaded, swipeCount, swipeBetsTotal } = defineProps<Props>();
const emits = defineEmits(["refetch-swipes"]);

const updateData = ref<IUpdateFilterData[]>([]);
const filterData = ref<IfilterData[]>([]);
const selections = ref<ISelection[]>([]);
const playerMetaData = ref<IfilterData[]>([]);

const handleFilterOpen = () => {
	if (!auth.loggedIn) {
		if (settings.isNativeApp) return window.promptLogin();
		auth.modals.login = true;
	} else {
		swipeFilterModal.value = "leagues";
	}
};

const reactiveAuth = computed(() => {
	return auth.loggedIn;
});

const handleFilterChange = (selectedLeagues: ISelection[]) => {
	selections.value = selectedLeagues;

	updateData.value = [];
	selectedLeagues.forEach((item: ISelection) => {
		const { sport, leagueId, regionId } = item;

		updateData.value.push({
			sportName: sport,
			regionNames: [
				{
					region: regionId,
					leagueNames: [leagueId],
				},
			],
		});
	});

	mutate();
	swipeFilterModal.value = false;
};

const { mutate } = useMutation<ISwipeHeaderData>({
	mutationFn: () =>
		api({
			url: "/Widget/SwipeBets?skip=0&limit=10&source={source}",
			type: "api",
			options: {
				method: "PUT",
				headers: {
					"X-Region-Code": settings.config?.brandSuffix as string,
					"Content-Type": "application/json",
				},
			},
			data: updateData.value,
		}),
	onSuccess: (data: ISwipeHeaderData) => {
		emits("refetch-swipes", data.data);
		swipeFilterModal.value = false;
	},
});

useQuery({
	queryKey: ["swipe-bet-meta"],
	queryFn: () =>
		api({
			url: "/Widget/SwipeBets/MetaData",
			type: "api",
			options: {
				method: "GET",
			},
		}),
	onSuccess: (data: IfilterData[]) => {
		filterData.value = data;
	},
});

useQuery({
	queryKey: ["swipe-player-metaData"],
	queryFn: () =>
		api({
			url: `/Widget/SwipeBets/PlayerMetaData`,
			type: "api",
			options: {
				method: "GET",
				headers: {},
			},
		}),
	onSuccess: (data: IfilterData[]) => {
		playerMetaData.value = data;

		for (const data of playerMetaData.value) {
			for (const region of data.regions) {
				for (const league of region.leagues) {
					const alreadyExists = selections.value.some(
						(item) => item.sport === data.name && item.leagueId === league.name && item.regionId === region.name
					);

					if (!alreadyExists) {
						selections.value = [...(selections.value || []), { sport: data.name, leagueId: league.name, regionId: region.name }];
					}
				}
			}
		}
	},
	enabled: reactiveAuth,
});
</script>

<template>
	<div class="flex justify-center w-full">
		<div class="max-w-[320px] min-w-[300px] flex justify-center mt-4">
			<div class="grid grid-cols-10">
				<div class="flex justify-center col-span-1">
					<div class="flex justify-center ml-3 place-items-center">
						<div class="rounded-lg shadow-xl bg-light-50 dark:bg-dark-700" :class="loaded ? '' : 'animate-pulse '">
							<p class="px-2 text-xs -py-2">
								{{ swipeCount }}/<span class="font-bold">{{ swipeBetsTotal }}</span>
							</p>
						</div>
					</div>
				</div>

				<div class="flex justify-center col-span-8 px-14 select-none">
					<LayoutSwipeBetLogo class="dark:text-light-50" />
				</div>

				<div class="flex items-center col-span-1">
					<div
						:disabled="!loaded"
						:class="loaded ? '' : 'animate-pulse '"
						@click="handleFilterOpen()"
						class="relative cursor-pointer inline-flex items-center p-3 text-sm rounded-xl font-medium text-center text-white hover:bg-primary-50 dark:hover:bg-primary-600/40 hover:shadow-2xl bg-light-50 dark:bg-dark-700"
					>
						<UiIcon :path="mdiFilterOutline" class="w-6 h-6 transition-all duration-200 fill-dark-600 dark:fill-light-50 group-hover:scale-110" />

						<div
							class="absolute inline-flex items-center text-light-50 justify-center w-5 h-5 text-xs font-bold text-white bg-primary-500 rounded-full -top-2 -end-2 dark:border-gray-900"
						>
							{{ selections.length }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<SwipeBetFilterModal
		v-if="!!swipeFilterModal"
		:active="swipeFilterModal"
		@handle-submit="(val) => handleFilterChange(val)"
		:data="filterData"
		:preSelections="selections"
		@set-active="
			(value) => {
				swipeFilterModal = value;
			}
		"
	/>
</template>
